import React, { useEffect, useRef } from 'react';
import { Flex, ChevronEndIcon, ChevronStartIcon, Dropdown } from '@fluentui/react-northstar';
import Styles from './table.module.css';

import { useTranslation } from 'react-i18next';
import { TICKET_ITEM_PER_PAGE } from '../../utils/constants';

interface Props {
  currentPage: number;
  totalCount: number;
  moveToPage: (type: string) => void;
  itemsPerPage: number;
  isLoading?: boolean;
  itemsPerPageChanged?: (itemPerPage: number) => void;
}

export const TablePagination = ({ currentPage, totalCount, moveToPage, itemsPerPage, isLoading = false, itemsPerPageChanged }: Props) => {
  const isNextDisabled = Math.ceil(totalCount / itemsPerPage) === currentPage || totalCount === 0 || isLoading;
  const isBackDisabled = currentPage <= 1 || isLoading;
  const { t } = useTranslation();
  const itemsPerPageRef = useRef<HTMLDivElement>(null);

  // Northstar's Dropdown focuses its list as it opens (handleStateChange) without preventScroll, and at that
  // moment the list still sits below the window - it is positioned above the button only afterwards. So the
  // browser scrolled the overflow: hidden app shell 52px to reach it, a scroll the user cannot undo. Focusing
  // this list never needs to scroll. Set after every render rather than on open: Northstar calls onOpenChange
  // after it has already focused the list.
  useEffect(() => {
    const list = itemsPerPageRef.current?.querySelector('ul');
    if (list) list.focus = (options?: FocusOptions) => HTMLElement.prototype.focus.call(list, { ...options, preventScroll: true });
  });
  const selectedItemsPerPage = TICKET_ITEM_PER_PAGE.filter(item => item.value === itemsPerPage.toString());

  const handleItemsPerPageChange = (_e, data) => {
    if (!itemsPerPageChanged) return;
    const selectedValue = Array.isArray(data?.value) ? data.value[0]?.value : data?.value?.value ?? data?.value;
    const parsedValue = parseInt(selectedValue?.toString(), 10);
    if (!isNaN(parsedValue)) itemsPerPageChanged(parsedValue);
  };

  return (
    <Flex  hAlign='end' vAlign='center' className='flex-grow' gap="gap.medium">
      <div className={Styles.paginationText}>
        <div className='flex items-center gap-2'>
          <div style={{width: "128px"}} ref={itemsPerPageRef}>
            <Dropdown items={TICKET_ITEM_PER_PAGE} checkable getA11ySelectionMessage={{ onAdd: item => `${item} has been selected.` }}
              value={selectedItemsPerPage}
              onChange={handleItemsPerPageChange}
              inline fluid className="pagination-items-per-page" />
          </div>
        </div>
      </div>
      <Flex vAlign='center' gap='gap.medium'>
        <span className={Styles.previousPage} onClick={() => { moveToPage('1') }}>
          <ChevronStartIcon outline size='small' disabled={isBackDisabled} styles={{ pointerEvents: isBackDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
          <ChevronStartIcon className={Styles.paginationText} outline size='small' disabled={isBackDisabled} styles={{ pointerEvents: isBackDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
        </span>
        <ChevronStartIcon className={Styles.paginationText} outline size='small' disabled={isBackDisabled} onClick={() => { moveToPage('previous') }} styles={{ pointerEvents: isBackDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
        
        <div className={Styles.paginationText}>{t('common.page')} {currentPage}</div>
        
        <ChevronEndIcon className={Styles.paginationText} outline size='small' disabled={isNextDisabled} onClick={() => { moveToPage('next') }} styles={{ pointerEvents: isNextDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
        <span className={Styles.previousPage} onClick={() => { moveToPage(Math.ceil(totalCount / itemsPerPage).toString()) }}>
          <ChevronEndIcon outline size='small' disabled={isNextDisabled} styles={{ pointerEvents: isNextDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
          <ChevronEndIcon className={Styles.paginationText} outline size='small' disabled={isNextDisabled} styles={{ pointerEvents: isNextDisabled ? 'none' : 'auto', '&:hover': { cursor: 'pointer' } }} />
        </span>
      </Flex>
    </Flex>
  );
};
